import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, Globe, Shield, Loader2, UserX, Check, X, Ban } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE = [
  { key: "everyone", label: "Todos" },
  { key: "following", label: "Só quem eu sigo" },
  { key: "nobody", label: "Ninguém" },
];

function AudienceRow({ label, value, options = AUDIENCE, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap py-2 border-b border-border/50 last:border-0" data-testid={testid}>
      <span className="text-sm">{label}</span>
      <div className="inline-flex rounded-full bg-muted p-0.5 text-xs">
        {options.map((o) => (
          <button key={o.key} type="button" onClick={() => onChange(o.key)} data-testid={`${testid}-${o.key}`}
            className={`px-3 py-1 rounded-full transition-colors ${value === o.key ? "bg-background shadow font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FollowRequestsList({ onChanged }) {
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => { try { setReqs((await axios.get(`${API}/follow-requests`)).data || []); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  const act = async (r, action) => {
    setBusy(r.request_id);
    try {
      await axios.post(`${API}/follow-requests/${r.request_id}/${action}`);
      setReqs((prev) => prev.filter((x) => x.request_id !== r.request_id));
      toast.success(action === "accept" ? `${r.user?.name} agora te segue` : "Solicitação recusada");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };
  if (!reqs.length) return <p className="text-xs text-muted-foreground" data-testid="follow-requests-empty">Nenhuma solicitação pendente.</p>;
  return (
    <div className="space-y-2" data-testid="follow-requests-list">
      {reqs.map((r) => (
        <div key={r.request_id} className="flex items-center gap-3 p-2 rounded-xl bg-muted/40" data-testid={`follow-request-${r.requester_id}`}>
          <Link to={`/user/${r.requester_id}`}><Avatar className="w-9 h-9"><AvatarImage src={r.user?.picture} /><AvatarFallback>{r.user?.name?.[0]}</AvatarFallback></Avatar></Link>
          <div className="flex-1 min-w-0">
            <Link to={`/user/${r.requester_id}`} className="text-sm font-semibold truncate block hover:underline">{r.user?.name}</Link>
            <span className="text-[11px] text-muted-foreground">quer seguir você</span>
          </div>
          <Button size="sm" className="rounded-full h-8" onClick={() => act(r, "accept")} disabled={busy === r.request_id} data-testid={`follow-request-accept-${r.requester_id}`}>
            {busy === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Aceitar
          </Button>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => act(r, "decline")} disabled={busy === r.request_id} data-testid={`follow-request-decline-${r.requester_id}`}>
            <X className="w-3.5 h-3.5" /> Recusar
          </Button>
        </div>
      ))}
    </div>
  );
}

function BlockedList() {
  const [items, setItems] = useState(null);
  const load = async () => { try { setItems((await axios.get(`${API}/blocks`)).data || []); } catch { setItems([]); } };
  useEffect(() => { load(); }, []);
  const unblock = async (u) => {
    try { await axios.delete(`${API}/users/${u.user_id}/block`); setItems((p) => p.filter((x) => x.user_id !== u.user_id)); toast.success(`${u.name} desbloqueado`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  if (items === null) return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  if (!items.length) return <p className="text-xs text-muted-foreground" data-testid="blocked-empty">Você não bloqueou ninguém.</p>;
  return (
    <div className="space-y-2" data-testid="blocked-list">
      {items.map((u) => (
        <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-xl bg-muted/40" data-testid={`blocked-user-${u.user_id}`}>
          <Avatar className="w-9 h-9"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
          <span className="flex-1 text-sm font-semibold truncate">{u.name}</span>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => unblock(u)} data-testid={`unblock-${u.user_id}`}><Ban className="w-3.5 h-3.5 mr-1" /> Desbloquear</Button>
        </div>
      ))}
    </div>
  );
}

export default function PrivacySettingsCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => { try { setPrefs((await axios.get(`${API}/privacy/settings`)).data); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (prefs && new URLSearchParams(window.location.search).get("tab") === "privacy") {
      document.getElementById("privacy")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [!!prefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/privacy/settings`, patch);
      setPrefs((p) => ({ ...p, ...r.data }));
      toast.success("Privacidade atualizada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setSaving(false);
  };

  if (!prefs) return null;
  return (
    <Card className="p-6" data-testid="privacy-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-slate-800 text-white flex items-center justify-center shrink-0"><Shield className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold tracking-tight">Privacidade e Segurança</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Controle quem vê e interage com você. {saving && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}</p>
        </div>
      </div>

      <div className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${prefs.is_private ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="privacy-private-row">
        <div className="flex items-center gap-3">
          {prefs.is_private ? <Lock className="w-5 h-5 text-amber-600" /> : <Globe className="w-5 h-5 text-emerald-600" />}
          <div>
            <Label className="text-sm font-semibold">{prefs.is_private ? "Perfil privado" : "Perfil público"}</Label>
            <p className="text-[11px] text-muted-foreground">
              {prefs.is_private ? "Só seguidores aprovados veem suas publicações. Novos seguidores precisam da sua aprovação." : "Qualquer pessoa pode ver, seguir e interagir com você."}
            </p>
          </div>
        </div>
        <Switch checked={!!prefs.is_private} onCheckedChange={(v) => update({ is_private: v })} data-testid="privacy-private-switch" />
      </div>

      <div className="mt-4">
        <AudienceRow label="Quem pode comentar minhas publicações" value={prefs.who_can_comment} onChange={(v) => update({ who_can_comment: v })} testid="privacy-comment" />
        <AudienceRow label="Quem pode me enviar mensagens" value={prefs.who_can_message} onChange={(v) => update({ who_can_message: v })} testid="privacy-message" />
        <AudienceRow label="Quem pode me marcar" value={prefs.who_can_tag} onChange={(v) => update({ who_can_tag: v })} testid="privacy-tag" />
        <AudienceRow label="Quem pode me seguir" value={prefs.who_can_follow} options={[{ key: "everyone", label: "Todos" }, { key: "nobody", label: "Ninguém" }]} onChange={(v) => update({ who_can_follow: v })} testid="privacy-follow" />
        <AudienceRow label="Visibilidade padrão das novas publicações" value={prefs.default_post_visibility} options={[{ key: "public", label: "Público" }, { key: "private", label: "Só seguidores" }]} onChange={(v) => update({ default_post_visibility: v })} testid="privacy-default-visibility" />
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><UserX className="w-4 h-4" /> Solicitações para seguir {prefs.pending_requests > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">{prefs.pending_requests}</span>}</h3>
          <FollowRequestsList onChanged={load} />
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Ban className="w-4 h-4" /> Usuários bloqueados</h3>
          <BlockedList />
        </div>
      </div>
    </Card>
  );
}
